import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import {
  clearPendingPayment,
  formatPortalCurrency,
  getPendingPayment,
  submitMockPayment,
} from '../mock/store';
import { elfPrimaryButtonSx } from '../elfTheme';

const methodLabel: Record<string, string> = {
  ach: 'Bank account (ACH)',
  card: 'Debit / credit card',
};

const LoanPayConfirm: React.FC = () => {
  const navigate = useNavigate();
  const draft = getPendingPayment();
  const [done, setDone] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState(0);

  if (!draft && !done) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No payment details found. Start from Make a payment.
        </Alert>
        <Button
          component={RouterLink}
          to="/portal/loans/pay"
          variant="contained"
        >
          Make a payment
        </Button>
      </Box>
    );
  }

  const handleSubmit = () => {
    if (!draft) return;
    setReceiptAmount(draft.amount);
    submitMockPayment(draft);
    setDone(true);
  };

  if (done) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Payment submitted
        </Typography>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Preview payment recorded successfully.
          </Alert>
          <Typography variant="h5" gutterBottom>
            {formatPortalCurrency(receiptAmount)}
          </Typography>
          <Typography color="text.secondary" paragraph>
            A confirmation email would be sent in the live system. This receipt
            is sample data only.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              component={RouterLink}
              to="/portal/loans/history"
              variant="contained"
              sx={elfPrimaryButtonSx}
            >
              View history
            </Button>
            <Button component={RouterLink} to="/portal/loans">
              Back to loan home
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Confirm payment
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Review your payment before submitting (preview).
      </Typography>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Amount
        </Typography>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {formatPortalCurrency(draft!.amount)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Method
        </Typography>
        <Typography sx={{ mb: 2 }}>{methodLabel[draft!.method]}</Typography>
        <Typography variant="body2" color="text.secondary">
          Type
        </Typography>
        <Typography sx={{ mb: 3 }}>
          {draft!.amountMode === 'installment'
            ? 'Scheduled installment'
            : 'Custom amount'}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={elfPrimaryButtonSx}
          >
            Submit payment
          </Button>
          <Button
            onClick={() => {
              clearPendingPayment();
              navigate('/portal/loans/pay');
            }}
          >
            Back
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoanPayConfirm;
